class RbMowerVideoStage extends HTMLElement {
  constructor() {
    super();
    this.frame = 0;
    this.stageTop = 0;
    this.stageHeight = 0;
    this.stageTrackHeight = 0;
    this.initialLeftInset = 0;
    this.initialRightInset = 0;
    this.initialRadius = 0;
    this.returnFocus = null;
    this.resizeObserver = null;
    this.reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.handleScroll = this.scheduleUpdate.bind(this);
    this.handleResize = this.measureAndUpdate.bind(this);
    this.handleDialogKeydown = this.onDialogKeydown.bind(this);
  }

  connectedCallback() {
    this.track = this.querySelector("[data-rb-mower-track]");
    this.sticky = this.querySelector("[data-rb-mower-sticky]");
    this.poster = this.querySelector("[data-rb-mower-poster]");
    this.dim = this.querySelector("[data-rb-mower-dim]");
    this.wash = this.querySelector("[data-rb-mower-wash]");
    this.openButton = this.querySelector("[data-rb-mower-open]");
    this.dialog = this.querySelector("[data-rb-mower-dialog]");
    this.mount = this.querySelector("[data-rb-mower-mount]");
    this.status = this.querySelector("[data-rb-mower-status]");
    this.closeControls = this.querySelectorAll("[data-rb-mower-close]");
    this.productWidthSource = document.querySelector(
      `product-info[data-product-id="${this.dataset.productId}"] .product`,
    );

    if (!this.track || !this.sticky || !this.poster || !this.productWidthSource) {
      return;
    }

    this.applyTuning();
    this.openButton?.addEventListener("click", () => this.openVideo());
    this.closeControls.forEach((control) => {
      control.addEventListener("click", () => this.closeVideo());
    });
    document.addEventListener("keydown", this.handleDialogKeydown);
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("resize", this.handleResize);
    this.reducedMotion.addEventListener("change", this.handleResize);

    if ("ResizeObserver" in window) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.productWidthSource);
      this.resizeObserver.observe(this.sticky);
    }

    this.measureAndUpdate();
    document.fonts?.ready.then(() => this.measureAndUpdate());
  }

  disconnectedCallback() {
    document.removeEventListener("keydown", this.handleDialogKeydown);
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("resize", this.handleResize);
    this.reducedMotion.removeEventListener("change", this.handleResize);
    this.resizeObserver?.disconnect();
    window.cancelAnimationFrame(this.frame);

    const video = this.mount?.querySelector("video");
    if (video) {
      video.pause();
      video.removeAttribute("src");
      video.load();
      video.remove();
    }
    document.body.classList.remove("rb-mower-video-dialog-open");
  }

  clamp(value, minimum = 0, maximum = 1) {
    return Math.min(maximum, Math.max(minimum, value));
  }

  smoothstep(value) {
    const progress = this.clamp(value);
    return progress * progress * (3 - 2 * progress);
  }

  setOpenButtonAvailable(isAvailable) {
    if (!this.openButton) {
      return;
    }

    this.openButton.style.pointerEvents = isAvailable ? "auto" : "none";

    if (isAvailable) {
      this.openButton.removeAttribute("tabindex");
      this.openButton.removeAttribute("aria-hidden");
    } else {
      if (document.activeElement === this.openButton) {
        this.openButton.blur();
      }
      this.openButton.setAttribute("tabindex", "-1");
      this.openButton.setAttribute("aria-hidden", "true");
    }
  }

  numberFromData(name, fallback) {
    const value = Number.parseFloat(this.dataset[name]);
    return Number.isFinite(value) ? value : fallback;
  }

  applyTuning() {
    this.washCoverage = this.numberFromData("washCoverage", 29);
    this.solidCoverage = this.numberFromData("solidCoverage", 2);
    this.dimCoverage = this.numberFromData("dimCoverage", 30);
    this.dimStrength = this.numberFromData("dimStrength", 10);

    const strongStop = 100 - this.washCoverage;
    const solidStop = 100 - this.solidCoverage;
    const subtleSpan = Math.min(20, Math.max(6, this.washCoverage * 0.36));
    const subtleStop = Math.max(0, strongStop - subtleSpan);
    const heavyStop = strongStop + Math.max(0, solidStop - strongStop) * 0.56;

    this.wash?.style.setProperty("--rb-mower-wash-subtle", `${subtleStop.toFixed(2)}%`);
    this.wash?.style.setProperty("--rb-mower-wash-strong", `${strongStop.toFixed(2)}%`);
    this.wash?.style.setProperty("--rb-mower-wash-heavy", `${heavyStop.toFixed(2)}%`);
    this.wash?.style.setProperty("--rb-mower-wash-solid", `${solidStop.toFixed(2)}%`);

    const dimStart = 100 - this.dimCoverage;
    const dimMid = dimStart + this.dimCoverage * (48 / 90);
    const dimEndAlpha = this.dimStrength / 100;
    const dimMidAlpha = dimEndAlpha * (8 / 28);

    this.dim?.style.setProperty("--rb-mower-dim-start", `${dimStart.toFixed(2)}%`);
    this.dim?.style.setProperty("--rb-mower-dim-mid", `${dimMid.toFixed(2)}%`);
    this.dim?.style.setProperty("--rb-mower-dim-mid-alpha", dimMidAlpha.toFixed(3));
    this.dim?.style.setProperty("--rb-mower-dim-end-alpha", dimEndAlpha.toFixed(3));
  }

  measure() {
    const stageRect = this.track.getBoundingClientRect();
    const shellRect = this.sticky.getBoundingClientRect();
    const widthRect = this.productWidthSource.getBoundingClientRect();

    this.stageTop = window.scrollY + stageRect.top;
    this.stageHeight = shellRect.height;
    this.stageTrackHeight = stageRect.height;
    this.initialLeftInset = Math.max(0, widthRect.left);
    this.initialRightInset = Math.max(0, window.innerWidth - widthRect.right);
    this.initialRadius = window.innerWidth <= 600 ? 18 : 30;
  }

  fadeBounds() {
    const viewportHeight = window.innerHeight;

    if (window.innerWidth <= 600) {
      const transitionDistance = Math.max(1, this.stageTrackHeight - this.stageHeight);
      const fadeStartOffset = Math.min(viewportHeight * 0.025, transitionDistance * 0.12);
      const fadeStart = this.stageTop + fadeStartOffset;
      return {
        fadeStart,
        fadeEnd: fadeStart + Math.max(1, transitionDistance * 0.84),
      };
    }

    const fadeStart = this.stageTop + viewportHeight * 0.12;
    const desiredFadeDistance = Math.max(viewportHeight * 0.78, this.stageHeight * 0.72);
    const availableFadeDistance = Math.max(
      this.stageHeight * 0.72,
      (this.stageTrackHeight - this.stageHeight) * 0.88,
    );

    return {
      fadeStart,
      fadeEnd: this.stageTop + Math.min(desiredFadeDistance, availableFadeDistance),
    };
  }

  update() {
    this.frame = 0;

    if (this.reducedMotion.matches || window.innerWidth <= 749) {
      const reducedClip = window.innerWidth <= 749
        ? "inset(0)"
        : `inset(0 ${this.initialRightInset.toFixed(2)}px 0 ${this.initialLeftInset.toFixed(2)}px round ${this.initialRadius}px)`;
      this.poster.style.clipPath = reducedClip;
      this.poster.style.webkitClipPath = reducedClip;
      this.wash.style.transform = "translate3d(0, 100%, 0)";
      this.wash.style.opacity = "0";
      this.dim.style.opacity = "0";
      if (this.openButton) {
        this.openButton.style.opacity = "1";
        this.setOpenButtonAvailable(true);
      }
      return;
    }

    const viewportHeight = window.innerHeight;
    const expandStart = this.stageTop - viewportHeight * 0.82;
    const expandEnd = this.stageTop - viewportHeight * 0.06;
    const expandProgress = this.smoothstep(
      (window.scrollY - expandStart) / Math.max(1, expandEnd - expandStart),
    );
    const leftInset = this.initialLeftInset * (1 - expandProgress);
    const rightInset = this.initialRightInset * (1 - expandProgress);
    const radius = this.initialRadius * (1 - expandProgress);
    const clipPath = `inset(0 ${rightInset.toFixed(2)}px 0 ${leftInset.toFixed(2)}px round ${radius.toFixed(2)}px)`;

    this.poster.style.clipPath = clipPath;
    this.poster.style.webkitClipPath = clipPath;

    const { fadeStart, fadeEnd } = this.fadeBounds();
    const fadeProgress = this.smoothstep(
      (window.scrollY - fadeStart) / Math.max(1, fadeEnd - fadeStart),
    );
    const washTranslate = 100 * (1 - fadeProgress);
    const washOpacity = this.clamp(fadeProgress * 1.45);
    const buttonFade = 1 - this.smoothstep((fadeProgress - 0.04) / 0.34);

    this.wash.style.transform = `translate3d(0, ${washTranslate.toFixed(2)}%, 0)`;
    this.wash.style.opacity = this.washCoverage > 0 ? washOpacity.toFixed(3) : "0";
    this.dim.style.opacity =
      this.dimCoverage > 0 && this.dimStrength > 0 ? fadeProgress.toFixed(3) : "0";

    if (this.openButton) {
      this.openButton.style.opacity = buttonFade.toFixed(3);
      this.setOpenButtonAvailable(buttonFade >= 0.08);
    }
  }

  scheduleUpdate() {
    if (!this.frame) {
      this.frame = window.requestAnimationFrame(() => this.update());
    }
  }

  measureAndUpdate() {
    this.measure();
    this.scheduleUpdate();
  }

  focusableElements() {
    return Array.from(
      this.dialog.querySelectorAll(
        'button:not([disabled]):not([tabindex="-1"]), [href]:not([tabindex="-1"]), video[controls]:not([tabindex="-1"]), [tabindex]:not([tabindex="-1"])',
      ),
    ).filter((element) => !element.hidden);
  }

  createVideo() {
    const video = document.createElement("video");
    const hlsUrl = this.dataset.videoHlsUrl;
    const mp4Url = this.dataset.videoMp4Url;
    const poster = this.querySelector(".rb-mower-video-stage__poster img");

    video.controls = true;
    video.playsInline = true;
    video.preload = "none";
    video.poster = poster?.currentSrc || poster?.src || "";
    video.setAttribute("aria-label", this.dataset.videoLabel || "Product film");

    if (hlsUrl && video.canPlayType("application/vnd.apple.mpegurl")) {
      video.src = hlsUrl;
    } else {
      video.src = mp4Url;
    }

    video.addEventListener(
      "loadeddata",
      () => {
        this.mount.classList.add("is-ready");
        if (this.status) {
          this.status.textContent = "";
        }
      },
      { once: true },
    );
    video.addEventListener(
      "error",
      () => {
        this.mount.classList.add("is-ready");
        if (this.status) {
          this.status.textContent = "The video could not be loaded. Please try again.";
        }
      },
      { once: true },
    );

    return video;
  }

  openVideo() {
    if (!this.dialog || !this.dataset.videoMp4Url) {
      return;
    }

    this.returnFocus = document.activeElement;
    this.dialog.hidden = false;
    document.body.classList.add("rb-mower-video-dialog-open");
    this.mount.classList.remove("is-ready");
    if (this.status) {
      this.status.textContent = "Loading video";
    }

    const video = this.createVideo();
    this.mount.querySelector("video")?.remove();
    this.mount.append(video);
    this.dialog.querySelector(".rb-mower-video-dialog__close")?.focus();

    const playAttempt = video.play();
    playAttempt?.catch(() => {});
  }

  closeVideo() {
    const video = this.mount?.querySelector("video");
    if (video) {
      video.pause();
      video.removeAttribute("src");
      video.load();
      video.remove();
    }

    if (this.dialog) {
      this.dialog.hidden = true;
    }
    document.body.classList.remove("rb-mower-video-dialog-open");
    this.mount?.classList.remove("is-ready");
    if (this.status) {
      this.status.textContent = "";
    }

    if (this.returnFocus instanceof HTMLElement) {
      this.returnFocus.focus();
    }
  }

  onDialogKeydown(event) {
    if (!this.dialog || this.dialog.hidden) {
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      this.closeVideo();
      return;
    }

    if (event.key !== "Tab") {
      return;
    }

    const focusable = this.focusableElements();
    if (!focusable.length) {
      return;
    }

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }
}

if (!customElements.get("rb-mower-video-stage")) {
  customElements.define("rb-mower-video-stage", RbMowerVideoStage);
}
